<script setup>
import { ref, computed } from "vue";
import { router } from "@inertiajs/vue2";
import { closeModal } from "../../Composables/useCloseModal";
import CRMAutomationForm from "./CRMAutomationForm.vue";

// Props

const props = defineProps({
    crmClient: {
        type: Object,
        required: true,
    },
    relationships: {
        type: Array,
        default: () => [],
        required: true,
    },
    errors: {
        type: Object,
        default: () => {},
        required: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

// Reactive State

const newCampaign = ref(false);
const confirmEnroll = ref(false);
const confirmResume = ref(false);
const confirmStop = ref("");
const pauseSelection = ref(null);

// Emits

const emit = defineEmits(["toast", "hide"]);

// Computed

const enrolledCampaign = computed(() => {
    return props.crmClient.active_campaign;
});

const campaignStatusOptions = computed(() => {
    const campaignStatus = enrolledCampaign.value?.status;

    return [
        {
            text: "Stop",
            icon: "stop",
            value: campaignStatus.canceled,
            action: () => {
                if (campaignStatus.canceled) return;
                confirmStop.value = "canceled";
            },
        },
        {
            text: "Pause",
            icon: "pause",
            value:
                campaignStatus.paused ||
                campaignStatus.responsive ||
                campaignStatus.waitlist,
            action: () => {
                if (campaignStatus.paused) return;
                confirmStop.value = "paused";
            },
        },
        {
            text: "Resume",
            icon: "play_arrow",
            value: campaignStatus.active,
            action: () => {
                if (campaignStatus.active) return;
                confirmResume.value = true;
            },
        },
        {
            text: "Preview",
            icon: "visibility",
            value: false,
            action: () => {
                emit("hide");
                router.get(
                    `/automations/${enrolledCampaign.value.id}/preview`,
                    {
                        next: 1,
                    }
                );
            },
        },
    ];
});

// Methods

function resume() {
    reset(true);

    router.put(
        route("campaignSubscriptions.resume", enrolledCampaign.value.id),
        { status: "active" },
        {
            onSuccess: (page) => {
                emit("toast", page.props.successMessage);
            },
            onError: (error) => {
                console.log(error);
            },
        }
    );
}

function stop(status = "paused") {
    reset(true);

    router.put(
        route("campaignSubscriptions.stop", enrolledCampaign.value.id),
        { status: status },
        {
            preserveScroll: true,
            onSuccess: (page) => {
                emit("toast", page.props.successMessage);
            },
            onError: (error) => {
                console.log(error);
            },
        }
    );
}

function reset(wait = false) {
    if (wait) {
        closeModal(() => {
            newCampaign.value = false;
            confirmEnroll.value = false;
            confirmStop.value = false;
            confirmResume.value = false;
            pauseSelection.value = null;
        });
        return;
    }

    newCampaign.value = false;
    confirmEnroll.value = false;
    confirmStop.value = false;
    confirmResume.value = false;
    pauseSelection.value = null;
}
</script>

<template>
    <div>
        <div class="flex flex-col gap-4">
            <v-btn
                v-if="!enrolledCampaign"
                small
                rounded
                block
                elevation="0"
                class="soli-grey"
                activeClass="primary--text"
                :disabled="disabled"
                @click="newCampaign = true"
                ><v-icon small left>add</v-icon>New Automation</v-btn
            >
            <v-card
                v-if="enrolledCampaign"
                flat
                outlined
                class="soli-round pa-4"
            >
                <div class="flex flex-col gap-2">
                    <div class="flex flex-col">
                        <div class="flex justify-between items-center gap-2">
                            <div class="soli-grey text-sm font-semibold">
                                {{ enrolledCampaign.campaign.name }}
                            </div>
                            <!--<v-btn
                                x-small
                                outlined
                                rounded
                                color="accent"
                                @click="
                                    enrolledCampaign.status.active
                                        ? (confirmStop = true)
                                        : (confirmResume = true)
                                "
                                ><v-icon x-small class="mr-1">{{
                                    enrolledCampaign.status.active
                                        ? "pause"
                                        : "play_arrow"
                                }}</v-icon
                                >{{
                                    enrolledCampaign.status.active
                                        ? "Pause"
                                        : "Resume"
                                }}</v-btn
                            >-->
                            <v-menu left offset-y>
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        v-bind="attrs"
                                        v-on="on"
                                        x-small
                                        icon
                                        color="dark"
                                        ><v-icon>more_vert</v-icon></v-btn
                                    >
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-for="status in campaignStatusOptions"
                                        :key="status.text"
                                        :input-value="status.value"
                                        :disabled="disabled"
                                        color="accent"
                                        @click="status.action"
                                        ><v-icon left small>{{
                                            status.icon
                                        }}</v-icon
                                        >{{ status.text }}</v-list-item
                                    >
                                </v-list>
                            </v-menu>
                        </div>
                        <div class="soli-grey caption">
                            Category: {{ enrolledCampaign.campaign.category }}
                        </div>
                        <div class="soli-grey caption text-capitalize">
                            {{
                                `${enrolledCampaign.studentable.first_name} | ${
                                    enrolledCampaign.program?.title
                                } | ${
                                    enrolledCampaign.instrument?.name ??
                                    "Not sure"
                                }`
                            }}
                        </div>
                    </div>
                    <v-divider />
                    <div
                        v-if="enrolledCampaign.progress"
                        class="flex flex-col gap-2"
                    >
                        <div class="flex justify-between items-center gap-2">
                            <div class="caption soli-grey">
                                {{ enrolledCampaign.progress?.name }}
                            </div>
                            <v-chip
                                :color="enrolledCampaign.status.color"
                                chip
                                x-small
                                class="capitalize"
                            >
                                {{ enrolledCampaign.status.value }}
                            </v-chip>
                        </div>

                        <v-progress-linear
                            :value="enrolledCampaign.progress?.stat"
                            color="secondary"
                            height="6"
                            rounded
                        />
                        <div
                            v-if="
                                !['paused', 'responsive', 'waitlist'].includes(
                                    enrolledCampaign.status.value
                                )
                            "
                            class="caption soli-grey"
                        >
                            {{
                                enrolledCampaign.previous_campaign_message_id
                                    ? "Next Message: "
                                    : "First Message: "
                            }}
                            {{
                                `${moment(
                                    enrolledCampaign.next_send_date
                                ).format("ddd, M/D")} at ${moment(
                                    enrolledCampaign.next_send_date
                                ).format("h:mma")}`
                            }}
                        </div>
                    </div>
                </div>
            </v-card>
            <v-simple-table
                v-if="crmClient.past_campaigns.length"
                dense
                class="soli-grey"
            >
                <thead>
                    <tr>
                        <th>Past Automations</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="campaignSubscription in crmClient.past_campaigns"
                        :key="campaignSubscription.id"
                    >
                        <td>{{ campaignSubscription.campaign.name }}</td>
                        <td>
                            <v-chip chip x-small class="capitalize">
                                {{ campaignSubscription.status.value }}
                            </v-chip>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </div>
        <CRMAutomationForm
            v-if="newCampaign"
            :crmClient="crmClient"
            :relationships="relationships"
            :errors="errors"
            @reset="reset()"
            @success="emit('toast', $event)"
        />
        <SoliChoice
            v-if="confirmResume"
            title="Are you sure?"
            :subtitle="`This will resume the ${enrolledCampaign?.campaign?.name} campaign for ${crmClient.first_name} and begin sending automated messages, starting now.`"
            button1="Confirm"
            button2="Nevermind"
            persistent
            attach=".z-more"
            class="crm-z-more"
            @option1="resume()"
            @option2="reset(true)"
            @reset="reset()"
        />
        <SoliChoice
            v-if="confirmStop"
            title="Are you sure?"
            :subtitle="`This will ${
                confirmStop == 'paused' ? 'pause' : 'cancel'
            } ${crmClient.first_name}'s ${
                enrolledCampaign?.campaign?.name
            } and automated messages.`"
            button1="Confirm"
            button2="Nevermind"
            persistent
            attach=".z-more"
            class="crm-z-more"
            @option1="stop(confirmStop)"
            @option2="reset(true)"
            @reset="reset()"
        />
        <!--<SoliChoice
            v-if="confirmStop"
            title="Are you sure?"
            :subtitle="`This will ${
                confirmStop == 'paused' ? 'pause' : 'cancel'
            } ${crmClient.first_name}'s ${
                enrolledCampaign?.campaign?.name
            } and automated messages.`"
            button1="Confirm"
            button2="Nevermind"
            persistent
            :disabled1="confirmStop == 'paused' && !pauseSelection"
            attach=".z-more"
            class="crm-z-more"
            @option1="stop(pauseSelection?.value ?? confirmStop)"
            @option2="reset(true)"
            @reset="reset()"
            ><slot>
                <v-container v-if="confirmStop == 'paused'">
                    <v-select
                        v-model="pauseSelection"
                        :items="[
                            {
                                text: 'Responsive',
                                value: 'responsive',
                            },
                            {
                                text: 'Waitlist',
                                value: 'waitlist',
                            },
                        ]"
                        item-text="text"
                        return-object
                        hide-details
                        label="Pause Reason"
                        outlined
                        dense
                /></v-container>
            </slot>
        </SoliChoice>-->
    </div>
</template>

<style scoped>
::v-deep th {
    padding: 0 0 !important;
}

::v-deep td {
    padding-top: 0px !important;
    padding-right: 16px !important;
    padding-bottom: 0px !important;
    padding-left: 0px !important;
}

.crm-z-more {
    z-index: 249;
}
</style>
